import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Card,
  Form,
  Input,
  DatePicker,
  Button,
  Spin,
  Modal,
} from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import AppLayout from '../components/common/Layout';
import FormResponseView from '../components/form/FormResponseView';
import { reportApi } from '../api/reportApi';
import { meetingApi } from '../api/adminApi';
import { useApi } from '../hooks/useApi';
import { formatDate } from '../utils/dateHelpers';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { confirm } = Modal;

const MeetingNew = () => {
  const [searchParams] = useSearchParams();
  const reportId = searchParams.get('reportId');
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [meetingForm] = Form.useForm();
  const { loading, execute } = useApi();

  useEffect(() => {
    const fetchReport = async () => {
      if (!reportId) {
        setFetchLoading(false);
        return;
      }
      try {
        const response = await reportApi.getById(reportId);
        setReport(response.data.report);
        meetingForm.setFieldsValue({ nextMeetingDate: dayjs().add(14, 'day') });
      } catch {
        setReport(null);
      } finally {
        setFetchLoading(false);
      }
    };
    fetchReport();
  }, [reportId, meetingForm]);

  const handleSubmit = (values) => {
    confirm({
      title: 'Сохранить итог встречи?',
      icon: <ExclamationCircleOutlined />,
      okText: 'Сохранить',
      onOk: async () => {
        await execute(
          () =>
            meetingApi.create({
              reportId,
              summary: values.summary,
              actionItems: values.actionItems,
              nextMeetingDate: values.nextMeetingDate.toISOString(),
            }),
          {
            successMessage: 'Итог встречи сохранён',
            onSuccess: () => navigate('/meetings'),
          }
        );
      },
    });
  };

  if (fetchLoading) {
    return (
      <AppLayout>
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      </AppLayout>
    );
  }

  if (!report) {
    return (
      <AppLayout>
        <Card>
          <Text>Отчёт не найден.</Text>
          <Button type="link" onClick={() => navigate('/reports')}>
            К отчётам
          </Button>
        </Card>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="page-header">
        <Title level={3}>Итог встречи</Title>
        <Text type="secondary">
          {report.teamMemberId?.fullName} — {formatDate(report.fillDate || report.createdAt)}
        </Text>
      </div>

      <Card title="Ответы сотрудника" style={{ marginBottom: 24 }}>
        <FormResponseView form={report} />
      </Card>

      <Card title="Заполните итог встречи">
        <Form form={meetingForm} layout="vertical" onFinish={handleSubmit} style={{ maxWidth: 600 }}>
          <Form.Item name="summary" label="Основные выводы" rules={[{ required: true }]}>
            <TextArea rows={4} />
          </Form.Item>
          <Form.Item name="actionItems" label="Договорённости" rules={[{ required: true }]}>
            <TextArea rows={4} />
          </Form.Item>
          <Form.Item name="nextMeetingDate" label="Дата следующей встречи" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} size="large">
              Сохранить итог
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </AppLayout>
  );
};

export default MeetingNew;
